import {
  IonAccordion,
  IonAccordionGroup,
  IonItem,
  IonLabel,
  IonList,
} from '@ionic/react';
import { useFormikContext } from 'formik';
import { ReactElement, useMemo } from 'react';
import { useGetUnitOfMeasureSystem } from '../../../../../../api/hooks/user-preference.hooks';
import i18n, { i18nKeyType } from '../../../../../../i18n';
import { getLocalizedText } from '../../../../../../i18n/i18n.utils';
import { NumericInputWidget } from '../../../../shared/component/widget/numeric-input';
import { PickerInput } from '../../../../shared/component/widget/picker-input';
import { SliderInput } from '../../../../shared/component/widget/slider-input';
import { SwitchInput } from '../../../../shared/component/widget/switch-input';
import { NumericFormat } from '../../../../shared/component/widget/widget.types';
import { SystemUnit } from '../../../../shared/model/flow.types';
import { mapUnitOfMeasureToLabel } from '../../../../shared/util/math';
import { BeaconWeldingData } from '../../model/beacon-welding-data';
import { mapFieldsToUomRequirements } from './weld-equipment-settings.hooks';

interface WeldEquipmentSettingsProps {
  equipment: BeaconWeldingData.Welder;
  configPrefix: string;
}

const WeldEquipmentSettings = (
  props: WeldEquipmentSettingsProps
): ReactElement | null => {
  const { equipment, configPrefix } = props;

  const weldSettings = useMemo(() => {
    if (equipment === BeaconWeldingData.Welder.MILLER_DELTAWELD) {
      return (
        <MillerDeltaWeldSettings
          fieldNamePrefix={`${configPrefix}.welder.customFields`}
        />
      );
    }
  }, [configPrefix, equipment]);

  if (!weldSettings) {
    return null;
  }

  return (
    <IonAccordionGroup>
      <IonAccordion>
        <IonItem
          slot="header"
          style={{ fontWeight: 'bold', fontSize: 'medium' }}
        >
          <IonLabel>
            {getLocalizedText('welding:welder_configuration')}
          </IonLabel>
        </IonItem>
        <div slot="content">{weldSettings}</div>
      </IonAccordion>
    </IonAccordionGroup>
  );
};

interface MillerDeltaWeldSettingsProps {
  fieldNamePrefix: string;
}

const MillerDeltaWeldSettings = (
  props: MillerDeltaWeldSettingsProps
): ReactElement => {
  const { fieldNamePrefix } = props;

  const { getFieldMeta } = useFormikContext();

  const startModeMeta = getFieldMeta(`${fieldNamePrefix}.sequenceStartMode`);
  const sequenceStartModeIsManual =
    startModeMeta.value ===
    BeaconWeldingData.MillerDeltaWeldSequenceStartMode.MANUAL;

  const runInModeMeta = getFieldMeta(`${fieldNamePrefix}.sequenceRunInMode`);
  const sequenceRunInModeIsManual =
    runInModeMeta.value ===
    BeaconWeldingData.MillerDeltaWeldSequenceRunInMode.MANUAL;

  const system = useGetUnitOfMeasureSystem();
  const { sequenceRetractDistance } =
    BeaconWeldingData.MillerDeltaWeldConfigCustomFieldUom;
  const uomConversion = mapFieldsToUomRequirements(system, {
    sequenceRetractDistance,
  });

  const retractDistanceLabel = getUomLabel(
    'sequenceRetractDistance',
    'welding:sequence_retract_distance',
    system
  );

  return (
    <IonList>
      <PickerInput
        name={`${fieldNamePrefix}.sequenceRunInMode`}
        label={getLocalizedText('welding:sequence_run_in_mode')}
        options={[
          {
            label: i18n.t('common:off'),
            value: BeaconWeldingData.MillerDeltaWeldSequenceRunInMode.OFF,
          },
          {
            label: i18n.t('common:auto'),
            value: BeaconWeldingData.MillerDeltaWeldSequenceRunInMode.AUTO,
          },
          {
            label: i18n.t('common:manual'),
            value: BeaconWeldingData.MillerDeltaWeldSequenceRunInMode.MANUAL,
          },
        ]}
      />
      {sequenceRunInModeIsManual && (
        <NumericInputWidget
          name={`${fieldNamePrefix}.sequenceRunInWfs`}
          label={getLocalizedText('welding:sequence_run_in_wfs')}
          format={NumericFormat.Percentage}
        />
      )}
      <PickerInput
        name={`${fieldNamePrefix}.sequenceStartMode`}
        label={getLocalizedText('welding:sequence_start_mode')}
        options={[
          {
            label: i18n.t('common:off'),
            value: BeaconWeldingData.MillerDeltaWeldSequenceStartMode.OFF,
          },
          {
            label: i18n.t('common:auto'),
            value: BeaconWeldingData.MillerDeltaWeldSequenceStartMode.AUTO,
          },
          {
            label: i18n.t('common:manual'),
            value: BeaconWeldingData.MillerDeltaWeldSequenceStartMode.MANUAL,
          },
        ]}
      />
      {sequenceStartModeIsManual && (
        <>
          <SliderInput
            name={`${fieldNamePrefix}.sequenceStartWfs`}
            label={getLocalizedText('welding:sequence_start_wfs')}
            min={50}
            max={250}
            step={1}
            format={NumericFormat.Percentage}
          />
          <SliderInput
            name={`${fieldNamePrefix}.sequenceStartVoltage`}
            label={getLocalizedText('welding:sequence_start_voltage')}
            min={50}
            max={150}
            step={1}
            format={NumericFormat.Percentage}
          />
          <SliderInput
            name={`${fieldNamePrefix}.sequenceStartArcLength`}
            label={getLocalizedText('welding:sequence_start_arc_length')}
            min={50}
            max={150}
            step={1}
            format={NumericFormat.Percentage}
          />
          <SliderInput
            name={`${fieldNamePrefix}.sequenceStartDwellTime`}
            label={getLocalizedText('welding:sequence_start_dwell_time')}
            min={0.1}
            max={0.5}
            step={0.1}
          />
          <SliderInput
            name={`${fieldNamePrefix}.sequenceStartRampTime`}
            label={getLocalizedText('welding:sequence_start_ramp_time')}
            min={0.1}
            max={5}
            step={0.1}
          />
        </>
      )}
      <NumericInputWidget
        name={`${fieldNamePrefix}.sequenceBurnBackTime`}
        label={getLocalizedText('welding:sequence_burn_back_time')}
      />
      <SwitchInput
        name={`${fieldNamePrefix}.sequenceRetractEnable`}
        label={getLocalizedText('welding:sequence_retract_enable')}
      />
      <SliderInput
        name={`${fieldNamePrefix}.sequenceRetractDistance`}
        label={retractDistanceLabel}
        step={0.1}
        min={uomConversion.sequenceRetractDistance.min}
        max={uomConversion.sequenceRetractDistance.max}
      />
    </IonList>
  );
};

const getUomLabel = (
  key: string,
  localizedLabel: i18nKeyType,
  system: SystemUnit
): string => {
  const prefix = getLocalizedText(localizedLabel);
  const uomSuffix = getUomSuffix(key, system);
  return `${prefix} (${uomSuffix})`;
};

const getUomSuffix = (key: string, system: SystemUnit): string | undefined => {
  const systemValues =
    BeaconWeldingData.MillerDeltaWeldConfigCustomFieldUom[key].uom.system;
  if (systemValues === undefined) {
    return;
  }

  return mapUnitOfMeasureToLabel(systemValues[system]);
};

export { WeldEquipmentSettings };
